/**
 * Which organization (and unit) Nest staff are looking at, carried in the address (A-35): `?org=<id>` (or `org=all`)
 * and `&unit=<id>` on every page of a tenant's apps, so a page can be bookmarked, shared and reached with the back
 * button. The session (one HttpOnly cookie) stays the authority the backends read; the shell keeps the two in step:
 *
 * - a tenant's app with no `org` in the address: the address takes the session's (nothing changes);
 * - a tenant's app whose address names another organization or unit: the session switches to it (the page waits);
 * - Ops is the platform: it has no organization, so opening it lets go of the one in view.
 *
 * No I/O here: the shell runs the plan (Shell.tsx), and these rules are tested (test/staffScope.test.ts).
 */

/** An organization (null = all organizations) and, within one, a unit (null = all its units). */
export interface StaffScope {
  org: string | null;
  unit: string | null;
}

/** What kind of app the page belongs to: Ops (platform), a tenant's app, or neither (My account; customers). */
export type ScopeApp = 'platform' | 'tenant' | 'none';

export type ScopePlan =
  | { kind: 'none' }
  /** Put the session's scope in the address (replacing the history entry). */
  | { kind: 'write'; search: string }
  /** Switch the session to the address's scope; the page waits for it. */
  | { kind: 'switch'; to: StaffScope }
  /** Ops: let go of the organization in view. */
  | { kind: 'clear' };

const ID = /^[A-Za-z0-9-]{1,100}$/;
const ALL = 'all';

/** The scope an address names, or undefined when it names none (or something that isn't an id). */
export function readStaffScope(search: string): StaffScope | undefined {
  const p = new URLSearchParams(search);
  const org = p.get('org');
  if (org === null || (org !== ALL && !ID.test(org))) return undefined;
  if (org === ALL) return { org: null, unit: null };
  const unit = p.get('unit');
  return { org, unit: unit !== null && ID.test(unit) ? unit : null };
}

/** The address's query string with this scope in it; every other parameter is kept as it was. */
export function staffScopeSearch(search: string, scope: StaffScope | null): string {
  const p = new URLSearchParams(search);
  p.delete('org');
  p.delete('unit');
  if (scope) {
    p.set('org', scope.org ?? ALL);
    if (scope.org && scope.unit) p.set('unit', scope.unit);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Where picking an organization lands: its Organization dashboard (A-35). */
export function tenantHome(orgId: string): string {
  return `/org${staffScopeSearch('', { org: orgId, unit: null })}`;
}

const same = (a: StaffScope, b: StaffScope) => a.org === b.org && a.unit === b.unit;

/**
 * What to do for this page. `refused` is a scope the session just refused to switch to (no such organization, or no
 * access): the address then takes the session's instead of trying again.
 */
/**
 * Where to go after signing in (owner, 2026-10-18): a session starts fresh, so a return page that names an
 * organization or unit (staff only carry them) is dropped and the landing decides (staff: Ops, All organizations).
 * Other return pages are kept.
 */
export function returnAfterSignIn(next: string | null): string | null {
  if (!next) return null;
  const q = next.indexOf('?');
  if (q < 0) return next;
  const params = new URLSearchParams(next.slice(q).split('#')[0]);
  return params.has('org') || params.has('unit') ? null : next;
}

export function planStaffScope(app: ScopeApp, search: string, session: StaffScope, refused?: StaffScope | null): ScopePlan {
  const url = readStaffScope(search);
  const raw = new URLSearchParams(search);
  if (app === 'platform') {
    if (session.org !== null) return { kind: 'clear' };
    return raw.has('org') || raw.has('unit') ? { kind: 'write', search: staffScopeSearch(search, null) } : { kind: 'none' };
  }
  if (app === 'none') return { kind: 'none' };
  if (!url || (refused && same(url, refused))) {
    const search2 = staffScopeSearch(search, session);
    return search2 === search ? { kind: 'none' } : { kind: 'write', search: search2 };
  }
  if (!same(url, session)) return { kind: 'switch', to: url };
  // Same scope; tidy a malformed unit parameter away.
  const tidy = staffScopeSearch(search, session);
  return tidy === search ? { kind: 'none' } : { kind: 'write', search: tidy };
}
